import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CardListItem,
  CardListItemProps
} from 'record-it-components/cardList/CardListItem/CardListItem';
import { CardListItemIconButton } from 'record-it-components/cardList/CardListItemIconButton/CardListItemIconButton';
import { CardListItemIconLink } from 'record-it-components/cardList/CardListItemIconLink/CardListItemIconLink';
import { CardListItemTitleWithSubtitle } from 'record-it-components/cardList/CardListItemTitleWithSubtitle/CardListItemTitleWithSubtitle';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { DateUtils } from 'record-it-utils/utils/DateUtils/DateUtils';
import { WorkTimeEntry } from '../../../classes/entities/WorkTimeEntry/workTimeEntryTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useWorkTimeEntryDeleteHandler } from '../../../hooks/entity/deleteHandler/useWorkTimeEntryDeleteHandler';
import { useTimeTrackingActivityPathName } from '../../../hooks/entity/timeTrackingActivity/useTimeTrackingActivityPathName';

export type WorkTimeEntryListItemProps =
  PropsWithoutChildren<CardListItemProps> & {
    workTimeEntry: WorkTimeEntry;
  };

export const WorkTimeEntryListItem: FunctionComponent<WorkTimeEntryListItemProps> =
  React.memo((props) => {
    const { workTimeEntry, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const { t } = useTranslation();

    const handleDeleteClick = useWorkTimeEntryDeleteHandler(workTimeEntry);
    const activityPathName = useTimeTrackingActivityPathName(
      workTimeEntry.timeTrackingActivityId
    );

    return (
      <CardListItem {...remainingProps}>
        <CardListItemTitleWithSubtitle data-testid="WorkTimeEntryListItem.TitleWithSubtitle">
          {() => ({
            title:
              activityPathName ??
              t('components.workTime.WorkTimeEntryListItem.noActivity'),
            subtitle: DateUtils.withCurrentTimezone().formatDateRange(
              workTimeEntry.startDate,
              workTimeEntry.endDate
            )
          })}
        </CardListItemTitleWithSubtitle>
        <CardListItemIconButton
          {...iconConfigurations.prefixed.listItem.deleteIcon}
          onClick={handleDeleteClick}
          data-testid="WorkTimeEntryListItem.DeleteButton"
        ></CardListItemIconButton>
        <CardListItemIconLink
          to={RoutePaths.editWorkTimeEntry(workTimeEntry.id)}
          {...iconConfigurations.prefixed.listItem.editInExtraPageIcon}
        ></CardListItemIconLink>
      </CardListItem>
    );
  });
